/**
 * PrintFileIssueReasonsSection - Configure print file issue reasons per organization
 *
 * Self-contained component that manages its own data fetching.
 * Follows the same pattern as RedoReasonsSection.
 *
 * Features:
 * - Add/remove/reorder reasons
 * - Auto-generates snake_case value from label
 * - Reset to defaults button
 * - Touch-friendly (44px+ targets)
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Loader2,
  Save,
  Plus,
  X,
  ChevronUp,
  ChevronDown,
  RotateCcw,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { VALID_PRINT_FILE_ISSUE_REASONS } from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

interface PrintFileIssueReason {
  value: string;
  label: string;
  description: string;
}

const DEFAULT_PRINT_FILE_ISSUE_REASONS: PrintFileIssueReason[] = [
  { value: 'wrong_design', label: 'Wrong Design', description: 'Incorrect artwork or design file for this SKU' },
  { value: 'missing_artwork', label: 'Missing Artwork', description: 'No print file found for this product' },
  { value: 'color_mismatch', label: 'Color Mismatch', description: "Print colors don't match the expected design" },
  { value: 'low_resolution', label: 'Low Resolution', description: 'Image quality too low, pixelated or blurry' },
  { value: 'wrong_position', label: 'Wrong Position', description: 'Design is wrong size or positioned incorrectly' },
  { value: 'other', label: 'Other Issue', description: 'Other print file or design data problem' },
];

// BLU-651 D3: reason VALUES are constrained to the canonical shared set —
// every downstream consumer (re-check gate, print-time auto-resolve
// allowlist, ResolveDialog branch routing) keys off these exact strings, so a
// free-form value would create issues nothing can ever resolve. The default
// label/description for a value seeds the add form; both stay editable.
const DEFAULT_BY_VALUE = new Map(DEFAULT_PRINT_FILE_ISSUE_REASONS.map((r) => [r.value, r]));

interface PrintFileIssueReasonsSectionProps {
  embedded?: boolean;
}

export function PrintFileIssueReasonsSection({ embedded = false }: PrintFileIssueReasonsSectionProps) {
  const { t } = useTranslation('settings');
  const [reasons, setReasons] = useState<PrintFileIssueReason[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // New reason form state — value picked from the canonical set (D3), label
  // and description prefilled from the value's default but editable.
  const [newValue, setNewValue] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newDescription, setNewDescription] = useState('');

  // Query
  const reasonsQuery = trpc.productionSettings.getPrintFileIssueReasons.useQuery();

  // Mutation
  const updateMutation = trpc.productionSettings.updatePrintFileIssueReasons.useMutation({
    onSuccess: () => {
      toast.success(t('production.printFileIssueReasons.saved'));
      setHasChanges(false);
      reasonsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to save: ${error.message}`);
    },
  });

  // Sync local state with server data
  useEffect(() => {
    if (reasonsQuery.data) {
      setReasons(reasonsQuery.data.reasons);
      setHasChanges(false);
    }
  }, [reasonsQuery.data]);

  // Canonical values not yet configured — the only ones addable (D3).
  const availableValues = VALID_PRINT_FILE_ISSUE_REASONS.filter(
    (value) => !reasons.some((r) => r.value === value),
  );

  const handleSelectValue = (value: string) => {
    setNewValue(value);
    const defaults = DEFAULT_BY_VALUE.get(value);
    // Prefill from the value's default copy; the operator can still edit both.
    setNewLabel(defaults?.label ?? value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));
    setNewDescription(defaults?.description ?? '');
  };

  const handleAdd = () => {
    const value = newValue;
    if (!value) return;

    if (!VALID_PRINT_FILE_ISSUE_REASONS.includes(value as (typeof VALID_PRINT_FILE_ISSUE_REASONS)[number])) {
      toast.error(t('production.printFileIssueReasons.invalidValue'));
      return;
    }

    if (reasons.some(r => r.value === value)) {
      toast.error(`A reason with value "${value}" already exists`);
      return;
    }

    if (reasons.length >= 20) {
      toast.error('Maximum of 20 print file issue reasons allowed');
      return;
    }

    const label = newLabel.trim() || DEFAULT_BY_VALUE.get(value)?.label || value;
    setReasons(prev => [...prev, { value, label, description: newDescription.trim() }]);
    setNewValue('');
    setNewLabel('');
    setNewDescription('');
    setHasChanges(true);
  };

  const handleRemove = (value: string) => {
    if (reasons.length <= 1) {
      toast.error('At least one print file issue reason is required');
      return;
    }
    setReasons(prev => prev.filter(r => r.value !== value));
    setHasChanges(true);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    setReasons(prev => {
      const next = [...prev];
      const temp = next[index]!;
      next[index] = next[index - 1]!;
      next[index - 1] = temp;
      return next;
    });
    setHasChanges(true);
  };

  const handleMoveDown = (index: number) => {
    if (index === reasons.length - 1) return;
    setReasons(prev => {
      const next = [...prev];
      const temp = next[index]!;
      next[index] = next[index + 1]!;
      next[index + 1] = temp;
      return next;
    });
    setHasChanges(true);
  };

  const handleResetToDefaults = () => {
    setReasons(DEFAULT_PRINT_FILE_ISSUE_REASONS);
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate({ reasons });
  };

  const isLoading = reasonsQuery.isLoading;

  const content = (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Configure the print file issue reasons available in the printer station dialog.
        The order here determines the display order.
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Existing reasons table */}
          {reasons.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-base">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium">Label</th>
                    <th className="text-left px-4 py-3 font-medium hidden sm:table-cell">Description</th>
                    <th className="w-28"></th>
                  </tr>
                </thead>
                <tbody>
                  {reasons.map((reason, index) => (
                    <tr key={reason.value} className="border-b last:border-b-0">
                      <td className="px-4 py-3">
                        <div>
                          <span className="font-medium">{reason.label}</span>
                          <span className="text-xs text-muted-foreground ml-2 font-mono">{reason.value}</span>
                        </div>
                        {/* Show description on mobile below the label */}
                        {reason.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 sm:hidden">{reason.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground hidden sm:table-cell">
                        {reason.description || '-'}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9"
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            aria-label={`Move ${reason.label} up`}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9"
                            onClick={() => handleMoveDown(index)}
                            disabled={index === reasons.length - 1}
                            aria-label={`Move ${reason.label} down`}
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9"
                            onClick={() => handleRemove(reason.value)}
                            aria-label={`Remove ${reason.label}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add new reason form — value constrained to the canonical set (D3):
              downstream resolve routing keys off these exact strings, so a
              free-form value would create issues nothing can resolve. */}
          <div className="flex flex-col sm:flex-row gap-2 items-end">
            <div className="flex-1 space-y-1">
              <Label htmlFor="new-print-issue-reason-value" className="text-sm text-muted-foreground">
                {t('production.printFileIssueReasons.reasonField')}
              </Label>
              <Select value={newValue} onValueChange={handleSelectValue} disabled={availableValues.length === 0}>
                <SelectTrigger id="new-print-issue-reason-value" className="h-11">
                  <SelectValue
                    placeholder={
                      availableValues.length === 0
                        ? t('production.printFileIssueReasons.allConfigured')
                        : t('production.printFileIssueReasons.selectReason')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableValues.map((value) => (
                    <SelectItem key={value} value={value}>
                      <span>{DEFAULT_BY_VALUE.get(value)?.label ?? value}</span>
                      <span className="ml-2 font-mono text-xs text-muted-foreground">{value}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 space-y-1">
              <Label htmlFor="new-print-issue-reason-label" className="text-sm text-muted-foreground">
                {t('production.printFileIssueReasons.newLabel')}
              </Label>
              <Input
                id="new-print-issue-reason-label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="e.g. Wrong Design"
                className="h-11"
                disabled={!newValue}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label htmlFor="new-print-issue-reason-desc" className="text-sm text-muted-foreground">
                Description (optional)
              </Label>
              <Input
                id="new-print-issue-reason-desc"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="e.g. Print file is corrupted or unreadable"
                className="h-11"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
              />
            </div>
            <Button
              variant="outline"
              className="h-11"
              onClick={handleAdd}
              disabled={!newValue}
            >
              <Plus className="h-4 w-4 mr-1" />
              {t('production.printFileIssueReasons.add')}
            </Button>
          </div>

          {/* Empty state */}
          {reasons.length === 0 && (
            <p className="text-sm text-muted-foreground/70 text-center py-2">
              No reasons configured. Add at least one print file issue reason.
            </p>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetToDefaults}
              className="text-muted-foreground"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              {t('production.printFileIssueReasons.resetToDefaults')}
            </Button>

            {hasChanges && (
              <Button
                onClick={handleSave}
                disabled={updateMutation.isPending || reasons.length === 0}
                className="h-12 gap-2"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {t('production.common.saveChanges')}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="border rounded-lg p-4">
      <h3 className="font-medium mb-3">Print File Issue Reasons</h3>
      {content}
    </div>
  );
}
